/** Task form for the "Follow Up" type. Adds a due date to the common fields. */

import { type Duration, format, startOfToday } from 'date-fns'
import { z } from 'zod'

import { useAppForm } from '@/frontend/components/form'
import { FieldWrapper } from '@/frontend/components/form/fields'
import { Button } from '@/frontend/components/ui/button'
import { PatientSearch } from '@/frontend/features/patient/patient-search'

import { useCreateTask } from '../use-create-task'
import {
  hasAssignee,
  hasClinicWhenRequired,
  TASK_ASSIGNMENT_DEFAULTS,
  TaskAssignmentFieldGroup,
  taskAssignmentShape,
} from './task-assignment.field-group'

const DUE_DATE_PRESETS: Duration[] = [{ days: 1 }, { days: 3 }, { days: 7 }]

const followUpFormSchema = z
  .object({
    patientId: z.string().nullable(),
    title: z.string().min(1, 'Title is required'),
    dueDate: z.date({ message: 'Due date is required' }),
    ...taskAssignmentShape,
  })
  .refine(hasAssignee, {
    message: 'Please select a team or staff member',
    path: ['assigneeRole'],
  })
  .refine(hasClinicWhenRequired, {
    message: 'Please select a clinic',
    path: ['assigneeClinicId'],
  })

type FollowUpFormValues = z.input<typeof followUpFormSchema>

/** Optional pre-populated values — e.g. when launched from a consultation. */
export interface FollowUpTaskFormDefaults {
  patientId?: string | null
  title?: string
}

export function FollowUpTaskForm(props: {
  defaultValues?: FollowUpTaskFormDefaults
  onSuccess?: (task: { id: string; patientId: string | null; title: string }) => void
  onCancel?: () => void
}) {
  const { mutateAsync: createTask } = useCreateTask()

  const form = useAppForm({
    defaultValues: {
      patientId: null,
      title: '',
      ...TASK_ASSIGNMENT_DEFAULTS,
      ...props.defaultValues,
    } as FollowUpFormValues,
    validators: {
      onSubmit: followUpFormSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      try {
        const { task } = await createTask({
          title: value.title,
          patientId: value.patientId,
          data: { type: 'follow_up', dueDate: format(value.dueDate!, 'yyyy-MM-dd') },
          assigneeRole: value.assigneeRole,
          assigneeClinicId: value.assigneeClinicId,
          assigneeStaffId: value.assigneeStaffId,
        })
        if (!task) throw new Error('Failed to create task')
        form.reset()
        props.onSuccess?.({ id: task.id, patientId: value.patientId, title: value.title })
      } catch (err) {
        formApi.setErrorMap({
          onSubmit: {
            form: err instanceof Error ? err.message : 'Failed to create task',
            fields: {},
          },
        })
      }
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <form.AppForm>
        <div className="flex flex-col gap-4 py-2">
          <form.GlobalError />

          {!props.defaultValues?.patientId && (
            <form.Field name="patientId">
              {(field) => (
                <FieldWrapper
                  field={field}
                  label="Patient"
                  render={({ id, disabled }) => (
                    <PatientSearch
                      id={id}
                      onChange={field.handleChange}
                      onBlur={field.handleBlur}
                      disabled={disabled}
                    />
                  )}
                />
              )}
            </form.Field>
          )}

          <form.AppField name="title">
            {(field) => (
              <field.InputField
                label="What are we following up on?"
                placeholder="Brief summary"
                required
              />
            )}
          </form.AppField>

          <form.AppField name="dueDate">
            {(field) => (
              <field.DatePickerField
                label="Due date"
                fromDate={startOfToday()}
                presets={DUE_DATE_PRESETS}
                required
              />
            )}
          </form.AppField>

          <TaskAssignmentFieldGroup
            form={form}
            fields={{
              assigneeRole: 'assigneeRole',
              assigneeClinicId: 'assigneeClinicId',
              assigneeStaffId: 'assigneeStaffId',
            }}
          />
        </div>

        <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <div className="flex items-center justify-end gap-2 pt-3">
              {props.onCancel && (
                <Button variant="outline" size="lg" type="button" onClick={props.onCancel}>
                  Cancel
                </Button>
              )}
              <Button type="submit" size="lg" disabled={!canSubmit || isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Task'}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </form.AppForm>
    </form>
  )
}
